import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Star, CheckCircle, Circle, Trophy, Zap, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Task {
    id: number;
    label: string;
    points: number;
    done: boolean;
    icon: string;
}

const RewardsTasksWidget: React.FC = () => {
    const { t } = useTranslation("nonHealth");
    const [tasks, setTasks] = useState<Task[]>([
        { id: 1, label: t("widgets.rewardsTasks.tasks.brushTeeth"), points: 10, done: true, icon: "🦷" },
        { id: 2, label: t("widgets.rewardsTasks.tasks.homework"), points: 20, done: false, icon: "📚" },
        { id: 3, label: t("widgets.rewardsTasks.tasks.exercise"), points: 15, done: false, icon: "🏃" },
        { id: 4, label: t("widgets.rewardsTasks.tasks.houseHelp"), points: 10, done: true, icon: "🏠" },
    ]);

    const totalPoints = tasks.filter((t) => t.done).reduce((s, t) => s + t.points, 0);
    const maxPoints = tasks.reduce((s, t) => s + t.points, 0);

    const toggle = (id: number) => setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));

    return (
        <motion.div
            key="rewards-tasks-widget"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
        >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-amber-200 relative overflow-hidden">
                {/* progress bar at top */}
                <motion.div
                    className="absolute top-0 left-0 h-1 bg-gradient-to-r from-amber-400 to-yellow-500"
                    animate={{ width: `${(totalPoints / maxPoints) * 100}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                />

                {/* header */}
                <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                        <motion.div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center" animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                            <Trophy className="w-4 h-4 text-amber-600" />
                        </motion.div>
                        <h5 className="text-amber-800 font-semibold text-base">{t("widgets.rewardsTasks.title")}</h5>
                    </div>
                    <motion.div
                        className="flex items-center gap-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-white px-2.5 py-1 rounded-full text-xs font-bold"
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 2, repeat: Infinity, repeatType: "mirror" }}
                    >
                        <Star className="w-3 h-3" />
                        {t("widgets.rewardsTasks.points", { count: totalPoints })}
                    </motion.div>
                </div>

                {/* progress card */}
                <div className="bg-gradient-to-br from-amber-50 to-yellow-100 p-3 rounded-xl mb-3 border border-amber-200">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-amber-700 text-xs font-medium">{t("widgets.rewardsTasks.todayProgress")}</span>
                        <span className="text-amber-800 font-bold text-sm">
                            {t("widgets.rewardsTasks.points", { count: totalPoints })}/{maxPoints}
                        </span>
                    </div>
                    <div className="w-full bg-amber-200 rounded-full h-3 overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full relative"
                            animate={{ width: `${(totalPoints / maxPoints) * 100}%` }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <motion.div
                                className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0"
                                animate={{ x: ["-100%", "100%"] }}
                                transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                            />
                        </motion.div>
                    </div>
                    {/* stars row */}
                    <div className="flex gap-1 mt-2 justify-center">
                        {[...Array(5)].map((_, i) => (
                            <motion.div key={i} animate={{ scale: i < Math.round((totalPoints / maxPoints) * 5) ? [1, 1.3, 1] : 1 }} transition={{ duration: 0.5, delay: i * 0.1 }}>
                                <Star className={`w-5 h-5 ${i < Math.round((totalPoints / maxPoints) * 5) ? "text-amber-400 fill-amber-400" : "text-gray-300"}`} />
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* tasks list */}
                <div className="space-y-2 mb-3">
                    {tasks.map((task) => (
                        <motion.div
                            key={task.id}
                            className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${task.done ? "bg-green-50 border-green-200" : "bg-white/70 border-gray-100"}`}
                            whileHover={{ scale: 1.02, x: -3 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => toggle(task.id)}
                        >
                            <motion.div animate={{ scale: task.done ? [1, 1.2, 1] : 1 }} transition={{ duration: 0.3 }}>
                                {task.done ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /> : <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />}
                            </motion.div>
                            <span className="text-lg">{task.icon}</span>
                            <span className={`flex-1 text-sm ${task.done ? "line-through text-gray-400" : "text-gray-700"}`}>{task.label}</span>
                            <div className="flex items-center gap-0.5 text-xs text-amber-600 font-bold">
                                <Zap className="w-3 h-3" />
                                {task.points}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* reward preview */}
                <motion.div className="bg-gradient-to-r from-amber-100 to-yellow-100 border border-amber-200 p-3 rounded-xl flex items-center gap-3 mb-3" whileHover={{ scale: 1.02 }}>
                    <motion.div className="text-2xl" animate={{ rotate: [0, 15, -15, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                        🎁
                    </motion.div>
                    <div className="flex-1">
                        <p className="text-amber-700 text-xs font-medium">{t("widgets.rewardsTasks.nextReward")}</p>
                        <p className="text-amber-800 font-bold text-sm">{t("widgets.rewardsTasks.rewards.iceCream")}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-amber-600 text-xs">{t("widgets.rewardsTasks.remaining")}</p>
                        <p className="text-amber-800 font-bold">
                            {maxPoints - totalPoints} {t("widgets.rewardsTasks.points", { count: 0 }).trim()}
                        </p>
                    </div>
                </motion.div>

                {/* add task button */}
                <motion.button
                    className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02, y: -2, boxShadow: "0 5px 15px rgba(251,191,36,0.4)" }}
                    whileTap={{ scale: 0.98 }}
                >
                    <Plus className="w-4 h-4" />
                    {t("widgets.rewardsTasks.addTask")}
                </motion.button>
            </div>
        </motion.div>
    );
};

export default RewardsTasksWidget;
